import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Filter, X, ChevronDown } from 'lucide-react';
import axios from 'axios';
import ProductCard from '../components/ProductCard';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ProductsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  // Dim the DNA helix on products page so products stand out
  useEffect(() => {
    const dna = document.querySelector('.dna-container');
    if (dna) dna.style.opacity = '0.15';
    return () => { if (dna) dna.style.opacity = ''; };
  }, []);

  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    minPrice: searchParams.get('minPrice') || '',
    maxPrice: searchParams.get('maxPrice') || '',
    sortBy: searchParams.get('sortBy') || 'created_at',
    order: searchParams.get('order') || 'desc'
  });

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (filters.search) params.append('search', filters.search);
        if (filters.minPrice) params.append('min_price', filters.minPrice);
        if (filters.maxPrice) params.append('max_price', filters.maxPrice);
        params.append('sort_by', filters.sortBy);
        params.append('order', filters.order);

        const { data } = await axios.get(`${API}/products?${params.toString()}`);
        setProducts(data);
      } catch (error) {
        console.error('Failed to fetch products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [filters]);

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    
    // Update URL params
    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([k, v]) => {
      if (v) params.set(k, v);
    });
    setSearchParams(params);
  };

  const clearFilters = () => {
    const cleared = {
      search: '',
      minPrice: '',
      maxPrice: '',
      sortBy: 'created_at',
      order: 'desc'
    };
    setFilters(cleared);
    setSearchParams({});
  };

  const hasActiveFilters = filters.search || filters.minPrice || filters.maxPrice;

  const sortOptions = [
    { label: 'Newest', value: 'created_at', order: 'desc' },
    { label: 'Price: Low to High', value: 'price', order: 'asc' },
    { label: 'Price: High to Low', value: 'price', order: 'desc' },
    { label: 'Top Rated', value: 'rating', order: 'desc' },
    { label: 'Name A-Z', value: 'name', order: 'asc' },
  ];

  const getCurrentSort = () => {
    const match = sortOptions.find(opt => opt.value === filters.sortBy && opt.order === filters.order);
    return match?.label || 'Sort By';
  };

  return (
    <div className="min-h-screen bg-[#050505] py-8" data-testid="products-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 
            className="text-3xl sm:text-4xl font-bold text-white mb-2"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="products-title"
          >
            Research Peptides
          </h1>
          <p className="text-[#A1A7B5]">
            Browse our complete catalog of lab-grade research peptides
          </p>
        </div>

        {/* Search and Filters Bar */}
        <div className="flex flex-col lg:flex-row gap-4 mb-8">
          {/* Search */}
          <div className="relative flex-1">
            <input
              type="text"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              placeholder="Search peptides..."
              className="input-field w-full pl-10"
              data-testid="products-search-input"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A7B5]" />
          </div>

          {/* Filter Toggle - Mobile */}
          <Button
            className="lg:hidden btn-secondary flex items-center justify-center space-x-2"
            onClick={() => setShowFilters(!showFilters)}
            data-testid="filter-toggle"
          >
            <Filter className="w-4 h-4" />
            <span>Filters</span>
          </Button>

          {/* Desktop Filters */}
          <div className="hidden lg:flex items-center space-x-4">
            {/* Sort Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="btn-secondary flex items-center space-x-2" data-testid="sort-dropdown">
                  <span>{getCurrentSort()}</span>
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="glass-card bg-[#0C0E14] border-[#1E2532]">
                {sortOptions.map((opt) => (
                  <DropdownMenuItem 
                    key={`${opt.value}-${opt.order}`}
                    onClick={() => {
                      handleFilterChange('sortBy', opt.value);
                      handleFilterChange('order', opt.order);
                    }}
                    className="text-white hover:text-[#00F0FF] hover:bg-white/5"
                  >
                    {opt.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button
                onClick={clearFilters}
                className="btn-secondary flex items-center space-x-2"
                data-testid="clear-filters"
              >
                <X className="w-4 h-4" />
                <span>Clear</span>
              </Button>
            )}
          </div>
        </div>

        {/* Mobile Filters Panel */}
        {showFilters && (
          <div className="lg:hidden glass-card p-4 mb-6 space-y-4" data-testid="mobile-filters">
            {/* Price Range */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[#A1A7B5] text-sm mb-2 block">Min Price</label>
                <input
                  type="number"
                  value={filters.minPrice}
                  onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                  placeholder="0"
                  className="input-field w-full"
                  data-testid="min-price-input"
                />
              </div>
              <div>
                <label className="text-[#A1A7B5] text-sm mb-2 block">Max Price</label>
                <input
                  type="number"
                  value={filters.maxPrice}
                  onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                  placeholder="1000"
                  className="input-field w-full"
                  data-testid="max-price-input"
                />
              </div>
            </div>

            {/* Sort */}
            <div>
              <label className="text-[#A1A7B5] text-sm mb-2 block">Sort By</label>
              <select
                value={`${filters.sortBy}-${filters.order}`}
                onChange={(e) => {
                  const [sortBy, order] = e.target.value.split('-');
                  handleFilterChange('sortBy', sortBy);
                  handleFilterChange('order', order);
                }}
                className="input-field w-full"
                data-testid="mobile-sort-select"
              >
                {sortOptions.map((opt) => (
                  <option key={`${opt.value}-${opt.order}`} value={`${opt.value}-${opt.order}`}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Clear Button */}
            {hasActiveFilters && (
              <Button onClick={clearFilters} className="btn-secondary w-full" data-testid="mobile-clear-filters">
                Clear All Filters
              </Button>
            )}
          </div>
        )}

        {/* Results Count */}
        <div className="mb-6">
          <p className="text-[#A1A7B5] text-sm" data-testid="results-count">
            Showing {products.length} product{products.length !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="glass-card p-4">
                <div className="skeleton h-48 w-full rounded-lg mb-4" />
                <div className="skeleton h-4 w-3/4 mb-2" />
                <div className="skeleton h-3 w-full mb-2" />
                <div className="skeleton h-6 w-1/4" />
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-16" data-testid="no-products">
            <p className="text-[#A1A7B5] text-lg mb-4">No products found</p>
            <Button onClick={clearFilters} className="btn-secondary">
              Clear Filters
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="products-grid">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductsPage;
